/**
 * GradeVector — a 7×2 visualization of the SDP-adjunction grade
 * vector. Each cell is one ℤ/2 component; lit cells glow.
 *
 * Used by BridgeConstructorPanel, GradeMonoidPanel, SnfWalkerPanel,
 * CompositionPanel, AttackSimulatorPanel.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { componentKey, PHASES, SDP_CODES, type Grade, type Phase, type SdpCode, } from "../algorithms/grade.js";
import { SDPS } from "../data/sdps.js";
import { panelTokens } from "@csk/demo-ui";

const SDP_COLOR: Record<SdpCode, string> = ((): Record<SdpCode, string> => {
  const out = {} as Record<SdpCode, string>;
  for (const s of SDPS) out[s.code] = s.color;
  return out;
})();

interface GradeVectorProps {
  readonly grade: Grade;
  readonly editable?: boolean;
  readonly onToggle?: (sdp: SdpCode, phase: Phase) => void;
  readonly label?: string;
  readonly compact?: boolean;
}

export function GradeVector({
  grade,
  editable = false,
  onToggle,
  label,
  compact = false,
}: GradeVectorProps): React.JSX.Element {
  const cell = compact ? 26 : 34;
  return (
    <div
      role="grid"
      aria-label={label ?? "Grade vector"}
      className="grid items-center gap-1 font-mono"
      style={{
        gridTemplateColumns: `28px repeat(${SDP_CODES.length}, ${cell}px)`,
      }}
    >
      <div aria-hidden />
      {SDP_CODES.map((s) => (
        <div
          key={`hdr-${s}`}
          className="text-center text-[11px] font-bold tracking-[0.05em]"
          style={{ color: SDP_COLOR[s] }}
        >
          {s}
        </div>
      ))}
      {PHASES.map((phase) => (
        <React.Fragment key={phase}>
          <div
            className="pr-1 text-right text-[12px] font-semibold text-[var(--tcc-muted)]"
            aria-hidden
          >
            {phase === "eta" ? "η" : "ε"}
          </div>
          {SDP_CODES.map((sdp) => {
            const key = componentKey(sdp, phase);
            const lit = (grade[key] ?? 0) === 1;
            const color = SDP_COLOR[sdp];
            const phaseSymbol = phase === "eta" ? "η" : "ε";
            const ariaLabel = `${sdp} ${phaseSymbol} ${lit ? "1" : "0"}`;
            const style: React.CSSProperties = {
              width: cell,
              height: cell,
              borderRadius: 7,
              border: `1px solid ${lit ? color : panelTokens.border}`,
              background: lit ? `${color}30` : "rgba(5,8,16,0.55)",
              boxShadow: lit
                ? `0 0 14px ${color}66, inset 0 0 8px ${color}44`
                : "none",
              color: lit ? color : panelTokens.muted,
              fontWeight: 700,
              fontSize: 12,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 120ms ease",
              padding: 0,
            };
            if (editable && onToggle) {
              return (
                <button
                  key={key}
                  type="button"
                  aria-label={ariaLabel}
                  aria-pressed={lit}
                  onClick={() => {
                    onToggle(sdp, phase);
                  }}
                  style={{ ...style, cursor: "pointer" }}
                >
                  {lit ? "1" : "0"}
                </button>
              );
            }
            return (
              <div
                key={key}
                role="gridcell"
                aria-label={ariaLabel}
                style={style}
              >
                {lit ? "1" : "0"}
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}
